<template>
  <div class="ai-agent-picker">
    <label class="ai-form-question">
      Which agent?
    </label>
    <p class="ai-form-hint">
      {{ hint }}
    </p>

    <div v-if="loading" class="ai-agent-picker__status" role="status">
      <b-spinner small class="mr-50" />
      <div>
        <p class="ai-agent-picker__status-title mb-0">Loading agents</p>
        <p class="ai-agent-picker__status-hint mb-0">Checking the voice agents you can use on this job.</p>
      </div>
    </div>

    <div
      v-else-if="agents.length"
      class="ai-agent-picker__group"
      role="radiogroup"
      aria-label="Voice agent for this job"
    >
      <label
        v-for="agent in agents"
        :key="agent.id"
        class="ai-agent-card"
        :class="{
          'ai-agent-card--active': selectedAgentId === agent.id,
          'ai-agent-card--disabled': disabled,
        }"
      >
        <input
          type="radio"
          class="ai-agent-card__radio"
          name="ai-calling-job-agent"
          :value="agent.id"
          :checked="selectedAgentId === agent.id"
          :disabled="disabled"
          @change="select(agent.id)"
        >
        <span class="ai-agent-card__text">
          <span class="ai-agent-card__label">
            {{ humanTitle(agent) }}
            <b-badge
              v-if="jobAgentId && agent.id === jobAgentId"
              class="ai-agent-card__badge ai-agent-card__default"
            >
              This job
            </b-badge>
            <b-badge
              v-else-if="orgAssignedId && agent.id === orgAssignedId"
              class="ai-agent-card__badge ai-agent-card__role"
            >
              Org default
            </b-badge>
          </span>
          <span v-if="showDescription(agent)" class="ai-agent-card__desc">
            {{ agent.description }}
          </span>
          <span v-if="agentCode(agent)" class="ai-agent-card__code">
            {{ agentCode(agent) }}
          </span>
        </span>
      </label>
    </div>

    <div
      v-else
      class="ai-agent-picker__empty"
    >
      <feather-icon icon="AlertCircleIcon" size="22" class="ai-agent-picker__empty-icon" />
      <p class="ai-agent-picker__empty-title">No agent ready</p>
      <p class="mb-50">{{ emptyMessage }}</p>
      <b-button
        v-if="allowRetry"
        variant="outline-secondary"
        :disabled="disabled"
        @click="$emit('retry')"
      >
        Try again
      </b-button>
    </div>

    <div v-if="showTypedFallback" class="ai-agent-picker__typed mt-75">
      <label class="ai-sub-label d-block mb-50" for="ai-agent-typed-id">
        Agent id
      </label>
      <b-form-input
        id="ai-agent-typed-id"
        :value="typedAgentId"
        :disabled="disabled"
        placeholder="Only if an admin gave you one"
        class="ai-script-input"
        @input="onTypedInput"
      />
    </div>
  </div>
</template>

<script>
import { BFormInput, BSpinner, BButton, BBadge } from 'bootstrap-vue'

function looksLikeAgentCode(value) {
  const s = (value || '').toString().trim()
  if (!s) return false
  return /^AGT[_-]/i.test(s) || /^[A-Z]{2,5}_[A-F0-9]{6,}$/i.test(s)
}

export default {
  name: 'AiCallingAgentPicker',
  components: { BFormInput, BSpinner, BButton, BBadge },
  props: {
    agents: { type: Array, default: () => [] },
    selectedAgentId: { type: String, default: '' },
    typedAgentId: { type: String, default: '' },
    orgAssignedId: { type: String, default: '' },
    jobAgentId: { type: String, default: '' },
    defaultAgentName: { type: String, default: '' },
    callType: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    emptyMessage: {
      type: String,
      default: 'No agents yet. Ask an admin to add one.',
    },
    allowRetry: { type: Boolean, default: false },
    showTypedFallback: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    hint: { type: String, default: 'Pick the one agent that will make this call.' },
  },
  methods: {
    select(id) {
      this.$emit('update:selectedAgentId', id)
      this.$emit('select', id)
    },
    onTypedInput(val) {
      this.$emit('update:typedAgentId', val)
      this.$emit('typed', val)
    },
    looksLikeAgentCode,
    agentKind(agent) {
      const raw = (
        (agent && (agent.callType || agent.call_type || agent.description))
        || this.callType
        || ''
      ).toString().trim().toLowerCase()
      if (raw === 'drive' || raw === 'follow_up' || raw.includes('drive')) return 'drive'
      if (raw === 'other' || raw.includes('other') || raw.includes('custom')) return 'other'
      return 'screening'
    },
    fallbackTitle(agent, isDefault) {
      const kind = this.agentKind(agent)
      if (kind === 'drive') return isDefault ? 'Default drive agent' : 'Drive follow-up agent'
      if (kind === 'other') return isDefault ? 'Default custom agent' : 'Custom call agent'
      return isDefault ? 'Default screening agent' : 'Screening agent'
    },
    humanTitle(agent) {
      const name = (agent && agent.name ? agent.name : '').toString().trim()
      if (name && !looksLikeAgentCode(name) && name !== (agent && agent.id)) return name
      if (name && !looksLikeAgentCode(name)) return name
      const isDefault = !!(this.orgAssignedId && agent && agent.id === this.orgAssignedId)
      const orgName = (this.defaultAgentName || '').toString().trim()
      if (isDefault && orgName && !looksLikeAgentCode(orgName)) return orgName
      return this.fallbackTitle(agent, isDefault)
    },
    agentCode(agent) {
      const id = (agent && agent.id ? agent.id : '').toString().trim()
      if (looksLikeAgentCode(id)) return id
      const name = (agent && agent.name ? agent.name : '').toString().trim()
      if (looksLikeAgentCode(name)) return name
      return ''
    },
    showDescription(agent) {
      const desc = (agent && agent.description ? agent.description : '').toString().trim()
      if (!desc || looksLikeAgentCode(desc)) return false
      const title = this.humanTitle(agent).toLowerCase()
      return !title.includes(desc.toLowerCase())
    },
  },
}
</script>

<style scoped>
.ai-agent-picker {
  --ai-brand: #00ac99;
  --ai-brand-soft: #e6f7f5;
  --ai-brand-border: rgba(0, 172, 153, 0.45);
}
.ai-form-question {
  display: block;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.35;
  margin-bottom: 0.3rem;
}
.ai-form-hint {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
  margin-bottom: 0.75rem;
}
.ai-sub-label {
  font-size: 14px;
  font-weight: 600;
  color: #2f2b3d;
}
.ai-agent-picker__status {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border-radius: 0.5rem;
  background: var(--ai-brand-soft);
  border: 1px dashed var(--ai-brand-border);
  color: #4b465c;
  font-size: 14px;
}
.ai-agent-picker__status-title {
  font-weight: 600;
  color: #2f2b3d;
}
.ai-agent-picker__status-hint {
  font-size: 13px;
  color: #6e6b7b;
}
.ai-agent-picker__empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  background: #f8f8f8;
  border: 1px dashed rgba(47, 43, 61, 0.16);
  border-radius: 0.5rem;
  color: #6e6b7b;
  font-size: 14px;
}
.ai-agent-picker__empty-icon {
  color: #00ac99;
  margin-bottom: 0.4rem;
}
.ai-agent-picker__empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #2f2b3d;
  margin-bottom: 0.25rem;
}
.ai-agent-picker__empty p {
  margin: 0;
}
.ai-agent-picker__group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.ai-agent-card {
  appearance: none;
  -webkit-appearance: none;
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  width: 100%;
  text-align: left;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0.65rem 0.85rem;
  border: 1.5px solid rgba(47, 43, 61, 0.1);
  border-radius: 0.45rem;
  background: #f5f5f5;
  color: #4b465c;
  cursor: pointer;
  box-shadow: none;
  font: inherit;
}
.ai-agent-card__radio {
  appearance: none;
  -webkit-appearance: none;
  width: 1.05rem;
  height: 1.05rem;
  margin: 0.15rem 0 0;
  flex-shrink: 0;
  border: 2px solid #c7c4d2;
  border-radius: 50%;
  background: #fff;
  cursor: inherit;
}
.ai-agent-card__text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}
.ai-agent-card__label {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  color: inherit;
}
.ai-agent-card__badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.1rem 0.35rem;
}
.ai-agent-card__role {
  background: #f3f2f7;
  color: #6e6b7b;
}
.ai-agent-card__default {
  background: var(--ai-brand-soft);
  color: #007a6e;
  border: none;
}
.ai-agent-card__desc {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
  color: #6e6b7b;
}
.ai-agent-card__code {
  font-size: 0.72rem;
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #b9b9c3;
}
.ai-agent-card:hover:not(.ai-agent-card--disabled):not(.ai-agent-card--active) {
  background: var(--ai-brand-soft);
  border-color: rgba(0, 172, 153, 0.4);
  color: #007a6e;
}
.ai-agent-card:hover:not(.ai-agent-card--disabled):not(.ai-agent-card--active) .ai-agent-card__desc {
  color: rgba(0, 140, 126, 0.9);
}
.ai-agent-card:hover:not(.ai-agent-card--disabled):not(.ai-agent-card--active) .ai-agent-card__code {
  color: rgba(0, 122, 110, 0.55);
}
.ai-agent-card:focus-within {
  outline: 2px solid var(--ai-brand);
  outline-offset: 2px;
}
.ai-agent-card--active {
  background: var(--ai-brand-soft);
  color: #007a6e;
  border-color: var(--ai-brand);
  box-shadow: 0 0 0 1px var(--ai-brand);
}
.ai-agent-card--active .ai-agent-card__radio {
  border-color: var(--ai-brand);
  background: var(--ai-brand);
  box-shadow: inset 0 0 0 2px #fff;
}
.ai-agent-card--active .ai-agent-card__label {
  font-weight: 600;
}
.ai-agent-card--active .ai-agent-card__desc {
  color: #4a8a84;
}
.ai-agent-card--active .ai-agent-card__code {
  color: rgba(0, 122, 110, 0.5);
}
.ai-agent-card--active .ai-agent-card__default {
  background: rgba(0, 172, 153, 0.18);
  color: #007a6e;
}
.ai-agent-card--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.ai-agent-picker__typed {
  padding: 0.15rem 0 0.25rem;
}
.ai-script-input {
  font-size: 15px;
  line-height: 1.45;
  min-height: 2.5rem;
}
</style>
